import { richTextToPlain } from './text.js';
/** Gesuchte Antworten ohne reine Darstellungs- und Hilfsdaten. */
export function taskAnswerCells(item) {
    return [...new Map(item.resolved.map((cell) => [cell.slot, cell])).values()].filter((cell) => {
        if (cell.role !== 'solution' || cell.value === null)
            return false;
        if (item.kind === 'money')
            return cell.slot.endsWith('.amount');
        if (item.kind === 'decomposition' && cell.slot.endsWith('.middle'))
            return false;
        if (item.kind === 'geoboard' && item.config.taskType === 'axis' && /^m\d+$/.test(cell.slot))
            return false;
        return true;
    });
}
function taskOptions(item, slot) {
    const row = slot.split('.')[0];
    if (item.kind === 'trueFalse')
        return ['richtig', 'falsch'].map((value) => ({ value, label: value }));
    if (item.kind === 'comparison')
        return ['<', '=', '>'].map((value) => ({ value, label: value }));
    if (item.kind === 'multipleChoice')
        return item.resolved
            .filter((c) => c.slot.startsWith(`${row}.o`))
            .map((c) => ({ value: String(c.value), label: String(c.value) }));
    if (item.kind === 'matching')
        return item.resolved
            .filter((c) => /^r\d+$/.test(c.slot))
            .map((c) => ({ value: c.slot.slice(1), label: String(c.value) }));
    if (/^(tenFrame|twentyFrame|hundredFrame)$/.test(item.kind) && /^p\d+$/.test(slot))
        return [
            { value: 'a', label: 'Blauer Punkt' },
            { value: 'b', label: 'Roter Punkt' },
            { value: 'x', label: 'Durchgestrichen' },
        ];
    if (item.kind === 'geoboard' && slot === 'axis')
        return [
            { value: 'vertical', label: 'senkrecht' },
            { value: 'horizontal', label: 'waagerecht' },
        ];
    return undefined;
}
/** Eingabeart eines Ziels; Bilder werden immer ausgewählt. */
function inputFor(item) {
    if (item.type === 'solutionImage')
        return 'choice';
    if (item.type === 'solutionText')
        return item.distractors.length > 0 ? 'dragDrop' : 'typed';
    return 'typed';
}
/** Alle Lösungsziele eines Dokuments in Seiten- und Feldreihenfolge. */
export function solutionTargets(document_) {
    const targets = [];
    document_.pages.forEach((page, pageIndex) => {
        for (const item of page.items) {
            if (item.hidden?.digital)
                continue;
            if (item.type === 'task') {
                const cells = item.kind === 'money'
                    ? item.resolved
                        .filter((c) => c.slot.endsWith('.amount'))
                        .map((c) => ({ ...c, role: 'solution' }))
                    : taskAnswerCells(item);
                for (const cell of cells) {
                    if (cell.value === null)
                        continue;
                    const options = taskOptions(item, cell.slot);
                    targets.push({
                        itemId: item.id,
                        slot: cell.slot,
                        expected: cell.value,
                        input: options
                            ? 'choice'
                            : cell.distractors && cell.distractors.length > 0
                                ? 'dragDrop'
                                : 'typed',
                        ...(options ? { options } : {}),
                        ...(item.kind === 'money'
                            ? {
                                format: item.resolved.find((original) => original.slot === cell.slot)?.role === 'given'
                                    ? 'payment'
                                    : 'money',
                                denominations: [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000].filter((v) => item.config.pieces === 'coins'
                                    ? v <= 200
                                    : item.config.pieces === 'notes'
                                        ? v >= 500
                                        : true),
                            }
                            : {}),
                        ...(item.kind === 'clock' && cell.slot.endsWith('.text')
                            ? { format: 'time' }
                            : {}),
                        pageIndex,
                        label: `Aufgabe ${page.items.indexOf(item) + 1}, Antwort ${cells.indexOf(cell) + 1}`,
                    });
                }
                continue;
            }
            if (item.type === 'solutionText') {
                targets.push({
                    itemId: item.id,
                    expected: item.answer,
                    ...(item.accept && item.accept.length > 0 ? { accept: item.accept } : {}),
                    ...(item.caseSensitive ? { caseSensitive: true } : {}),
                    input: inputFor(item),
                    pageIndex,
                    label: (() => {
                        const question = page.items
                            .slice(0, page.items.indexOf(item))
                            .reverse()
                            .find((candidate) => candidate.type === 'text');
                        return question?.type === 'text'
                            ? richTextToPlain(question.content)
                            : `Antwort ${page.items.indexOf(item) + 1}`;
                    })(),
                });
                continue;
            }
            if (item.type === 'solutionImage') {
                targets.push({
                    itemId: item.id,
                    expected: { assetId: item.correctAssetId },
                    input: 'choice',
                    options: [item.correctAssetId, ...item.distractorAssetIds]
                        .map((assetId) => ({
                        value: assetId,
                        assetId,
                        label: document_.assets[assetId]?.meta.name ?? 'Bild',
                    }))
                        .sort((a, b) => a.value.localeCompare(b.value)),
                    pageIndex,
                    label: `Bildantwort ${page.items.indexOf(item) + 1}`,
                });
            }
        }
    });
    return targets;
}
/** Schlüssel einer Antwort: Feld und – bei Aufgaben – die Zelle. */
export function targetKey(target) {
    return target.slot ? `${target.itemId}:${target.slot}` : target.itemId;
}
/** Vergleicht Eingaben nach dem Lernziel und den eingestellten Schreibregeln. */
export function isCorrect(target, answer) {
    if (answer === null || answer === undefined || answer === '')
        return false;
    if (typeof target.expected === 'object') {
        return String(answer) === target.expected.assetId;
    }
    const given = String(answer).trim();
    const money = (value) => {
        const match = value.trim().match(/^([0-9]+(?:[.,][0-9]{1,2})?)\s*(€|Euro|ct|Cent)?$/i);
        return match
            ? Math.round(Number(match[1].replace(',', '.')) * (/^(ct|cent)$/i.test(match[2] ?? '') ? 1 : 100))
            : null;
    };
    if (target.format === 'money')
        return money(given) !== null && money(given) === money(String(target.expected));
    if (target.format === 'payment') {
        const parts = given.split('+').map(money);
        return (parts.every((p) => p !== null && target.denominations?.includes(p)) &&
            parts.reduce((sum, p) => sum + (p ?? 0), 0) === money(String(target.expected)));
    }
    if (target.format === 'time') {
        const time = (s) => {
            const m = s.trim().match(/^(\d{1,2})[:.](\d{2})(?:\s*Uhr)?$/i);
            return m && Number(m[1]) < 24 && Number(m[2]) < 60
                ? `${Number(m[1]) % 12}:${Number(m[2])}`
                : null;
        };
        return time(given) !== null && time(given) === time(String(target.expected));
    }
    const numeric = (value) => /^[-+]?\d+(?:[.,]\d+)?$/.test(value.trim()) ? Number(value.replace(',', '.')) : Number.NaN;
    const accepted = [String(target.expected), ...(target.accept ?? [])];
    return accepted.some((value) => (target.caseSensitive
        ? value.trim().normalize('NFC') === given.normalize('NFC')
        : value.trim().normalize('NFC').toLocaleLowerCase('de-DE') ===
            given.normalize('NFC').toLocaleLowerCase('de-DE')) ||
        (Number.isFinite(numeric(value)) && numeric(value) === numeric(given)));
}
/** Beziehungsprüfung für offene Mauern und freie Reihenfolge bei Silbenwörtern. */
export function evaluateAnswers(document, answers) {
    const targets = solutionTargets(document);
    const checked = Object.fromEntries(targets.map((target) => [targetKey(target), isCorrect(target, answers[targetKey(target)])]));
    for (const page of document.pages)
        for (const item of page.items) {
            if (item.type !== 'task' || item.hidden?.digital)
                continue;
            const own = targets.filter((target) => target.itemId === item.id);
            if (item.kind === 'syllablePuzzle') {
                const remaining = own.map((target) => String(target.expected).toLocaleLowerCase('de-DE'));
                for (const target of own) {
                    const value = String(answers[targetKey(target)] ?? '')
                        .trim()
                        .toLocaleLowerCase('de-DE');
                    const index = remaining.indexOf(value);
                    checked[targetKey(target)] = index >= 0;
                    if (index >= 0)
                        remaining.splice(index, 1);
                }
            }
            if (item.kind === 'matching') {
                for (const target of own) {
                    const answer = String(answers[targetKey(target)] ?? '');
                    const expected = item.resolved.find((cell) => cell.slot === `r${String(target.expected)}`)?.value;
                    checked[targetKey(target)] =
                        /^\d+$/.test(answer) &&
                            expected !== undefined &&
                            item.resolved.find((cell) => cell.slot === `r${answer}`)?.value === expected;
                }
            }
            if (item.kind === 'numberWall') {
                const values = new Map(item.resolved.map((cell) => [
                    cell.slot,
                    cell.role === 'given'
                        ? Number(cell.value)
                        : Number(String(answers[`${item.id}:${cell.slot}`] ?? '').replace(',', '.')),
                ]));
                const filled = own.every((target) => String(answers[targetKey(target)] ?? '').trim() !== '');
                const levels = new Set(item.resolved.map((cell) => cell.slot.split('c')[0])).size;
                let valid = filled &&
                    [...values.values()].every((value) => Number.isInteger(value) &&
                        value >= item.config.range.min &&
                        value <= item.config.range.max);
                for (let row = 0; row < levels - 1; row++)
                    for (let col = 0; col < levels - row - 1; col++) {
                        const a = values.get(`r${row}c${col}`), b = values.get(`r${row}c${col + 1}`);
                        if (values.get(`r${row + 1}c${col}`) !==
                            (item.config.operation === 'mul' ? a * b : a + b))
                            valid = false;
                    }
                if (valid)
                    for (const target of own)
                        checked[targetKey(target)] = true;
            }
        }
    return checked;
}
/**
 * Wörter und Zahlen für die Lösungsleiste: die richtigen Lösungen der Seite
 * plus die Ablenker der Lösungsfelder. Optional sortiert oder
 * zusammengefasst (Assistenz, Phase 7.3).
 */
export function solutionBar(document_, pageIndex, options = {}) {
    const page = document_.pages[pageIndex];
    if (!page)
        return [];
    const entries = [];
    for (const item of page.items) {
        if (item.hidden?.digital)
            continue;
        if (item.type === 'solutionText') {
            entries.push(item.answer, ...item.distractors);
        }
        if (item.type === 'task') {
            for (const cell of item.resolved) {
                if (cell.role !== 'solution' || cell.value === null)
                    continue;
                if (cell.distractors && cell.distractors.length > 0) {
                    entries.push(String(cell.value), ...cell.distractors.map(String));
                }
            }
        }
    }
    const list = options.unique ? [...new Set(entries)] : entries;
    return options.sorted ? [...list].sort((a, b) => a.localeCompare(b, 'de-DE')) : list;
}
//# sourceMappingURL=targets.js.map